import React, { useEffect, useState, useRef } from "react";
import { connect, useDispatch } from "react-redux";
import { bool, shape, arrayOf, string } from "prop-types";
import { Field, Form, Formik } from "formik";
import { Col, Row } from "react-bootstrap";
import * as Yup from "yup";
import * as i18n from "util/i18n";
import * as numberUtils from "util/number";
import * as configUtils from "util/config";
import { validatePhone } from "util/form";
import useSchema from "hooks/useSchema";
import { selectors as settingsSelectors, actions as settingsActions } from "reducers/settings";
import { actions as accountsActions, selectors as accountsSelectors } from "reducers/accounts";
import Notification from "pages/_components/Notification";
import CustomButton from "pages/_components/CustomButton/CustomButton";
import MainContainer from "pages/_components/MainContainer";
import CustomProductHead from "pages/_components/CustomProductHead";
import ViewTitle from "pages/_components/header/components/ViewTitle";
import ProductSelector from "pages/_components/fields/formik/FormikProductSelector";
import AmountField from "pages/_components/fields/formik/AmountField";
import Selector from "pages/_components/fields/formik/Selector";
import PhoneInput from "pages/_components/fields/PhoneInput";
import TextField from "pages/_components/fields/TextField";
import CustomData from "pages/_components/CustomData/CustomData";
import FormattedAmount from "pages/_components/FormattedAmount";
import GeneralCredentialForm from "pages/_components/Forms/GeneralCredentialForm";
import { actions as notificationActions } from "reducers/notification";
import GridTable from "../../_components/GridTable/GridTable";
import { usePhoneAreaCodesArg } from "../../../hooks/usePhoneAreaCodesArg";
import { push } from "connected-react-router";

const CashOut = ({
    fetchingAccounts,
    accounts,
    isDesktop,
    clientLastName,
    clientName,
    document,
    cashOutSendStatus,
}) => {
    const dispatch = useDispatch();
    const formRef = useRef(null);
    const { stringRequired, numberRequired } = useSchema();
    const [availableAccounts, setAvailableAccounts] = useState([{ id: "1" }]);
    const [formConfirm, setFormConfirm] = useState(false);
    const [requestBody, setRequestBody] = useState(false);
    const [preview, setPreview] = useState(false);
    const [disablePersonDataForm, setDisablePersonDataForm] = useState(false);
    const [selectedAccountId, setSelectedAccountId] = useState(null);
    const [selectedAccountLabel, setSelectedAccountLabel] = useState("");
    const [isValidating, setIsValidating] = useState(true);
    // const [externalLink, setExternalLink] = useState(null);
    const [minAmount, setMinAmount] = useState(0);
    const [maxAmount, setMaxAmount] = useState(0);
    const [multiple, setMultiple] = useState(0);
    const { argAreaCodes } = usePhoneAreaCodesArg();

    const isCashoutLinkEnabled = configUtils.getBoolean("backend.link.cashout.enabled");
    const omaAmountMaxLength =  configUtils.get("ode.amount.field.maxlength", 16);
    const linkAmountMaxLength = configUtils.get("link.amount.field.maxlength", 22);

    useEffect(() => {
        if (isValidating && cashOutSendStatus === "INVALID_CREDENTIAL") {
            setIsValidating(false);
            formRef.current.setErrors({
                credential: i18n.get("returnCode.COR027W", "No ha presentado un token válido"),
            });
        }
    }, [isValidating, cashOutSendStatus]);

    useEffect(() => {
        // const link = configUtils.get("ode.external.link", "https://www.bibank.com.ar/");
        const min = configUtils.get("ode.amount.minimum.value", 1000);
        const max = configUtils.get("ode.amount.maximum.value", 100000);
        const mult = configUtils.getInteger("ode.amount.multiple.value", 100);
        setMinAmount(min);
        setMaxAmount(max);
        setMultiple(mult);
        // setExternalLink(link);
    }, []);

    const cleanForm = () => {
        setPreview(false);
        setFormConfirm(false);
        setDisablePersonDataForm(false);
        setSelectedAccountId(null);
        setSelectedAccountLabel("");
        // eslint-disable-next-line no-unused-expressions
        formRef?.current?.resetForm();
    };

    useEffect(() => {
        cleanForm();
        dispatch(accountsActions.listAccounts());
        dispatch(settingsActions.getUserData());
        dispatch(accountsActions.cleanCashOut());
    }, []);

    useEffect(() => {
        if (cashOutSendStatus && preview) {
            setPreview(false);
            setFormConfirm(true);
            dispatch(accountsActions.cleanCashOut());
        }
    }, [cashOutSendStatus]);

    useEffect(() => {
        const formatOption = (account) => ({
            balance: { currency: account?.currency, quantity: account?.availableBalance },
            currency: account?.currency,
            frequentDestination: null,
            id: account?.productId,
            label: `${i18n.get(`productType.${account?.accountType}`)} ····${account?.accountNumber?.slice(-4)}`,
            minimumPayment: 0,
            permissions: Object.keys(account?.permissions),
            statementsLoan: null,
            totalPayment: 0,
            type: account?.accountType,
            number: account?.accountNumber,
            selectedAccountLabel: `····${account?.accountNumber?.slice(-4)} ${account?.accountType} ${i18n.get(
                `currency.label.${account?.currency}`,
            )}`,
        });

        if (accounts?.length) {
            setAvailableAccounts(
                accounts
                    .filter((acc) => acc.currency === "ARS" && acc.accountType === "CC")
                    ?.map((acc) => formatOption(acc)),
            );
        }
    }, [accounts]);

    useEffect(() => {
        if (selectedAccountId) {
            setSelectedAccountLabel(
                availableAccounts.filter((acc) => acc.id === selectedAccountId)[0].selectedAccountLabel,
            );
        }
    }, [selectedAccountId]);

    const validateAmount = (amount) =>
        parseFloat(minAmount) <= amount && amount <= parseFloat(maxAmount) && amount % multiple === 0;
    // Formik Validation Control
    const FORM_ID = "cashOut";

    const formikProps = {
        validateOnChange: true,
        validateOnBlur: false,
        initialValues: {
            accountField: "",
            amountField: {
                amount: "",
                currency: "",
            },
            phoneNumber: {
                countryCode: "",
                areaCode: "",
                value: "",
            },
            nombre: "",
            apellido: "",
            documentNumber: "",
            credential: "",
            reference: "",
            isCashoutLinkEnabled,
        },
        validationSchema: () =>
            Yup.object().shape({
                accountField: stringRequired,
                amountField: Yup.object().shape({
                    amount: numberRequired.test(
                        "amount",
                        i18n.get("transfer.amount.error"), // eslint-disable-next-line func-names
                        (value) => validateAmount(value),
                    ),
                }),
                phoneNumber: validatePhone(argAreaCodes),
                nombre: stringRequired,
                apellido: stringRequired,
                documentNumber: Yup.string()
                    .matches(new RegExp(`^[\\d]{2,3}?[\\d]{3,3}?[\\d]{3,3}$`, "g"), i18n.get("global.incorrect.format"))
                    .required(i18n.get("global.error.required")),
                reference: Yup.string().when("isCashoutLinkEnabled", {
                    is: true,
                    then: stringRequired,
                    otherwise: Yup.string().notRequired(),
                }),
            }),
    };

    const currencies = [{ id: "ARS", label: i18n.get(`currency.label.ARS`) }];

    /* const renderMenuOption = () => {
        const buttonClassName = isDesktop ? "toolbar-item more-button" : "toolbar-item commands-mobile";
        return (
            isDesktop && (
                <>
                    <div className={buttonClassName} key="exportList">
                        <CustomButton
                            text={i18n.get("", "Sucursales RapiPago")}
                            func={() => window.open(externalLink)}
                            className="btn-primary flex cashout__sucursal-button"
                        />
                    </div>
                </>
            )
        );
    }; */

    const personOptions = [
        { id: 1, value: "Yo" },
        { id: 2, value: "Otra persona" },
    ];

    const handleChangePerson = (value) => {
        if (value === 1) {
            formRef.current.setFieldValue("nombre", `${clientName}`);
            formRef.current.setFieldValue("apellido", `${clientLastName}`);
            formRef.current.setFieldValue("documentNumber", document);
            setDisablePersonDataForm(true);
        } else {
            formRef.current.setFieldValue("nombre", "");
            formRef.current.setFieldValue("apellido", "");
            formRef.current.setFieldValue("documentNumber", "");
            setDisablePersonDataForm(false);
        }
    };

    const handleValidateAmount = (form) => {
        const { amount } = form.values.amountField;
        const account = availableAccounts.find((acc) => acc.id === selectedAccountId);

        if (account && amount && parseFloat(amount) > account.balance?.quantity && validateAmount(amount)) {
            dispatch(
                notificationActions.showNotification(i18n.get("cashout.amount.greaterthancurrentbalance"), "warning", [
                    "cashOut",
                ]),
            );
        }
    };

    const handleOnBack = () => {
        if (formConfirm) {
            setFormConfirm(false);
            dispatch(push("/cashOut"));
        }
        else
            dispatch(push("/desktop"));
    };

    const submitIsAllowed = (allFormik) => {
        const { accountField, amountField, phoneNumber, nombre, apellido, documentNumber } = allFormik.values;

        if (!accountField || !amountField || !phoneNumber || !nombre || !apellido || !documentNumber) {
            return false;
        }
        // const phoneNum = `${phoneNumber.countryCode}${phoneNumber.areaCode}${phoneNumber.value}`;
        // if (phoneNumber.prefix === "593") {
        //     if (!regexArsPhoneEcua.test(phoneNumber.value)) {
        //         return false;
        //     }
        // } else if (!regexArsPhone.test(phoneNum)) {
        //     return false;
        // }
        if (!allFormik.isValid) {
            return false;
        }
        return true;
    };

    const confirmCashout = (allFormik) => {
        const { values } = allFormik;
        if (values) {
            setPreview(true);
            const { amountField, phoneNumber, nombre, apellido, documentNumber, reference } = values;
            const { amount } = amountField;
            const { countryCode, areaCode, value } = phoneNumber;
            const account = availableAccounts.filter((acc) => acc.id === selectedAccountId)[0];
            const odeData = {
                accountNumber: account.number,
                accountProductId: account.id,
                amount,
                phoneNumber: `${countryCode}${areaCode}${value}`,
                nombre,
                apellido,
                documentNumber,
                reference,
                accountCurrency: account.currency,
                accountType: account.type,
            };
            const params = {
                odeData,
                amount: account.balance?.quantity,
            };
            setRequestBody({
                odeData,
                amount: {
                    currency: account?.currency,
                    quantity: amountField.amount,
                },
            });

            setFormConfirm(true);
        }
    };

    const getAmountMaxDigits = () => {
        return isCashoutLinkEnabled ? linkAmountMaxLength : omaAmountMaxLength;
    };

    const getDisclaimerMsg = () => {
        const source = isCashoutLinkEnabled ? 'link' : 'oma';
        return i18n.get(`cashout.confirm.${source}.disclaimer`);
    };

    return (
        <>
            <Notification scopeToShow="cashOut" />
            <CustomProductHead
                titleId="menu.cashout"
                onBack={handleOnBack}
                renderMenuOption={null}>
                <div className="toolbar-item view-title">
                   <ViewTitle id="menu.cashout" defaultValue="Extracción de efectivo" />
               </div>
            </CustomProductHead>
            <MainContainer showLoader={fetchingAccounts}>
                <Formik {...formikProps} ref={formRef}>
                    {(allFormik) => (
                        <Form id={FORM_ID}>
                            {formConfirm && <h2>{i18n.get("transfer.summary.confirm")}</h2>}
                            {!formConfirm && (
                                <Row className="form-background">
                                    <Col xs={12} md={10} mdOffset={1}>
                                        <Field
                                            idForm={FORM_ID}
                                            name="accountField"
                                            component={ProductSelector}
                                            label={`${i18n.get("", "Cuenta origen")}`}
                                            placeholder={i18n.get("deposits.request.filters.debitAccount.placeHolder")}
                                            data={{
                                                emptyOptionLabel: "",
                                                options: availableAccounts,
                                            }}
                                            onChange={setSelectedAccountId}
                                            disabled={!accounts.length}
                                        />
                                    </Col>
                                    <Col className="required" xs={12} md={5} mdOffset={1}>
                                        <Field
                                            component={AmountField}
                                            labelText={`${i18n.get("transfer.transferred")}`}
                                            name="amountField"
                                            data={{ options: currencies }}
                                            clearable={false}
                                            maxLength={getAmountMaxDigits()}
                                            disableSelect
                                            fixedDecimalScale
                                            onBlurChange={() => handleValidateAmount(allFormik)}
                                        />
                                        <div
                                            style={{
                                                marginTop: "-10px",
                                            }}>
                                            <span
                                                style={{
                                                    color: "var(--color-blue)",
                                                    fontSize: "var(--fontSize6)",
                                                }}>{`${i18n.get(
                                                "",
                                                "Debe ser un importe entre $",
                                            )} ${numberUtils.formatAmountStrDouble(minAmount)} ${i18n.get(
                                                "",
                                                "y $",
                                            )} ${numberUtils.formatAmountStrDouble(maxAmount)}`}</span>
                                            <br />
                                            <span
                                                style={{
                                                    marginTop: "-5px",
                                                    color: "var(--color-blue)",
                                                    fontSize: "var(--fontSize6)",
                                                }}>{`${i18n.get("", "Debe ser múltiplo de $")} ${multiple}`}</span>
                                        </div>
                                    </Col>
                                    <Col xs={12} md={5} mdOffset={6}>
                                        <Field
                                            idForm={FORM_ID}
                                            name="line"
                                            label={`${i18n.get("", "Elegí quién va a retirar el efectivo")}`}
                                            placeholder={i18n.get("global.select.option.placeholder")}
                                            component={Selector}
                                            options={
                                                personOptions &&
                                                personOptions.map((s) => ({
                                                    value: s.id,
                                                    label: s.value,
                                                }))
                                            }
                                            onChange={handleChangePerson}
                                        />
                                    </Col>
                                    <Col xs={12} md={5} mdOffset={1}>
                                        <Field
                                            idForm={FORM_ID}
                                            name="nombre"
                                            labelTextDefault={`${i18n.get("", "Nombre")}`}
                                            hidePlaceholder
                                            component={TextField}
                                            disabled={disablePersonDataForm}
                                        />
                                    </Col>
                                    <Col xs={12} md={5} mdOffset={6}>
                                        <Field
                                            idForm={FORM_ID}
                                            name="apellido"
                                            labelTextDefault={`${i18n.get("", "Apellido")}`}
                                            hidePlaceholder
                                            component={TextField}
                                            disabled={disablePersonDataForm}
                                        />
                                    </Col>
                                    <Col xs={12} md={5} mdOffset={1}>
                                        <Field
                                            idForm={FORM_ID}
                                            name="documentNumber"
                                            labelTextDefault={`${i18n.get("documentType.label.DNI")}`}
                                            hidePlaceholder
                                            component={TextField}
                                            disabled={disablePersonDataForm}
                                        />
                                    </Col>
                                    <Col xs={12} md={5} mdOffset={6}>
                                        <Field
                                            component={PhoneInput}
                                            idForm={FORM_ID}
                                            name="phoneNumber"
                                            country="AR"
                                            mobilePhone="54"
                                            enableSearch={false}
                                            preferredCountries={["ar"]}
                                            labelTextDefault={i18n.get("", "Celular")}
                                            hintLabel={i18n.get(
                                                "",
                                                "Debes incluir el código de área y luego el número.",
                                            )}
                                            onlyCountries={configUtils.get("onboarding.step.sms.countries").split("|")}
                                            // onlyCountries={["ar"]}
                                            // disableDropdown
                                        />
                                    </Col>
                                    {isCashoutLinkEnabled && (
                                        <Col xs={12} md={5} mdOffset={1}>
                                            <Field
                                                idForm={FORM_ID}
                                                name="reference"
                                                labelTextDefault={`${i18n.get("", "Referencia")}`}
                                                hidePlaceholder
                                                component={TextField}
                                                maxLength={20}
                                            />
                                        </Col>
                                    )}
                                </Row>
                            )}
                            {formConfirm && (
                                <GridTable className="cashout-list">
                                    <GridTable.Container>
                                        <GridTable.Data columnStart={1} rowStart={1}>
                                            <CustomData
                                                titleText={i18n.get("", "Cuenta origen")}
                                                titleTextType="TEXT"
                                                valueTextType="TEXT"
                                                valueText={selectedAccountLabel}
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data columnStart={isDesktop ? 2 : 1} rowStart={isDesktop ? 1 : 2}>
                                            <CustomData
                                                titleText={i18n.get("documentType.label.DNI")}
                                                titleTextType="TEXT"
                                                valueTextType="TEXT"
                                                valueText={allFormik?.values?.documentNumber}
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data columnStart={isDesktop ? 3 : 1} rowStart={isDesktop ? 1 : 3}>
                                            <CustomData
                                                titleText={i18n.get("", "Celular")}
                                                titleTextType="TEXT"
                                                valueTextType="TEXT"
                                                valueText={`+${allFormik?.values?.phoneNumber?.countryCode}${allFormik?.values?.phoneNumber?.areaCode}${allFormik?.values?.phoneNumber?.value}`}
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data columnStart={1} rowStart={isDesktop ? 2 : 4}>
                                            <CustomData
                                                titleText={i18n.get("", "Monto")}
                                                titleTextType="TEXT"
                                                valueTextType="TEXT"
                                                valueText={
                                                    <FormattedAmount
                                                        currency="ARS"
                                                        quantity={allFormik?.values?.amountField?.amount}
                                                    />
                                                }
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data columnStart={isDesktop ? 2 : 1} rowStart={isDesktop ? 2 : 5}>
                                            <CustomData
                                                titleText={i18n.get(`${FORM_ID}.confirm.name`)}
                                                titleTextType="TEXT"
                                                valueTextType="TEXT"
                                                valueText={allFormik?.values?.nombre}
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data columnStart={isDesktop ? 3 : 1} rowStart={isDesktop ? 2 : 6}>
                                            <CustomData
                                                titleText={i18n.get(`${FORM_ID}.confirm.lastname`)}
                                                titleTextType="TEXT"
                                                valueTextType="TEXT"
                                                valueText={allFormik?.values?.apellido}
                                            />
                                        </GridTable.Data>
                                        {isCashoutLinkEnabled && (
                                            <GridTable.Data columnStart={isDesktop ? 4 : 1} rowStart={isDesktop ? 1 : 7}>
                                                <CustomData
                                                    titleText={i18n.get("", "Referencia")}
                                                    titleTextType="TEXT"
                                                    valueTextType="TEXT"
                                                    valueText={allFormik?.values?.reference}
                                                />
                                            </GridTable.Data>
                                        )}
                                    </GridTable.Container>
                                </GridTable>
                            )}
                            {!formConfirm && (
                                <Row className="form-actions">
                                    <Col xs={12} md={4} mdOffset={4}>
                                        <CustomButton
                                            text={i18n.get("", "Continuar")}
                                            func={() => confirmCashout(allFormik)}
                                            className="btn-primary flex"
                                            disabled={!submitIsAllowed(allFormik)}
                                        />
                                    </Col>
                                </Row>
                            )}
                            {formConfirm && (
                                <>
                                    <div className="container--alert" role="alert">
                                        <div className="alert alert-info">
                                            <CustomData
                                                icon="information_fill.svg"
                                                titleText={getDisclaimerMsg()}
                                                keepSpaceIfNoValue={false}
                                            />
                                        </div>
                                    </div>
                                </>
                            )}
                        </Form>
                    )}
                </Formik>
                {formConfirm && (
                    <>
                        <GeneralCredentialForm
                            transactionData={requestBody}
                            type="otp"
                            action={accountsActions.cashOutSend}
                            idActivity="cashout.send"
                            hideIndications
                        />
                    </>
                )}
            </MainContainer>
        </>
    );
};

CashOut.propTypes = {
    isDesktop: bool.isRequired,
    fetchingAccounts: bool.isRequired,
    accounts: arrayOf(shape({})).isRequired,
    clientName: string.isRequired,
    clientLastName: string.isRequired,
    document: string.isRequired,
    generateCodeInProgress: bool.isRequired,
    cashOutSendStatus: string.isRequired,
};

const mapStateToProps = (state) => ({
    fetchingAccounts: accountsSelectors.getFetching(state),
    accounts: accountsSelectors.getAccounts(state),
    clientLastName: settingsSelectors.getLastName(state) || "",
    clientName: settingsSelectors.getName(state) || "",
    document: settingsSelectors.getDocument(state) || "",
    generateCodeInProgress: accountsSelectors.getGenerateCashOutCodeInProgress(state),
    cashOutSendStatus: accountsSelectors.getCashOutSendStatus(state),
});

export default connect(mapStateToProps)(CashOut);
